export interface AdvisoryData {
  advisories: AdvisoryItem[];
  crop_name: string;
  crop_image: string;
  crop_variety: string;
  sowing_date: string;
  state: string;
  district: string;
  transplantation_date: string | null;
}
export enum AdvisoryNames {
  Weather = "Weather",
  CropGrowth = "Crop Growth",
  Irrigation = "Irrigation",
  Spraying = "Spraying",
  SoilNutrients = "Soil Nutrients",
  PathogenDetection = "Pathogen Detection",
}
type AdvisoryName =
  | "Weather"
  | "Crop Growth"
  | "Irrigation"
  | "Spraying"
  | "Soil Nutrients"
  | "Pathogen Detection";

interface AdvisoryResultMap {
  Weather: WeatherAdvisoryResult;
  "Crop Growth": CropGrowthAdvisoryResult;
  Irrigation: IrrigationAdvisoryResult;
  Spraying: SprayingAdvisoryResult;
  "Soil Nutrients": SoilNutrientAdvisoryResult;
  "Pathogen Detection": PathogenDetectionAdvisoryResult;
}

interface BaseAdvisoryItem {
  id: number;
  advisory_time: string | null;
  advisory_name: AdvisoryName;
  subscription_id: string;
  created_at: string;
  updated_at: string;
}

// Discriminated union based on advisory_name
export type AdvisoryItem = {
  [K in AdvisoryName]: BaseAdvisoryItem & {
    advisory_name: K; // Narrow this key for union
    result_data: AdvisoryResultMap[K];
  };
}[AdvisoryName];

// export interface AdvisoryItem {
//     id: number;
//     advisory_time: string | null;
//     advisory_name:
//     | "Weather"
//     | "Crop Growth"
//     | "Irrigation"
//     | "Spraying"
//     | "Soil Nutrients";
//     result_data: any; // We'll make this strict below
//     subscription_id: string;
//     created_at: string;
//     updated_at: string;
// }

export interface WeatherAdvisoryResult {
  today: TodayWeather;
  hazard: HazardWeather;
  hourly: HourlyWeather;
  weekly: WeeklyWeather;
}

export interface TodayWeather {
  max: number;
  min: number;
  temperature: number;
  weather_icon: string;
  weather_type: string;
  wind: {
    icon: string;
    value: string;
  };
  humidity_percent: {
    icon: string;
    value: number;
  };
  rain_chance_percent: {
    icon: string;
    value: number;
  };
}

export interface HazardWeather {
  hazard_icon: string;
  hazard_image: string;
  weather_type: string;
  weather_until: string;
  alert: {
    type: string;
    icon: string;
    line: string;
  };
}

export interface HourlyWeather {
  alert: string;
  alert_sub: string;
  alert_icon: string;
  temparature: HourlyTemperature[];
}

export interface HourlyTemperature {
  icon: string;
  temp: number;
  time: string;
  rain_percent: number;
}

export interface WeeklyWeather {
  alert: string;
  alert_sub: string;
  alert_icon: string;
  temparature: WeeklyTemperature[];
}

export interface WeeklyTemperature {
  day: string;
  max: number;
  min: number;
  icon: string;
  rain_percent: number;
}

export interface CropGrowthAdvisoryResult {
  stages: string[];
  current_stage: number;
  advisory_image: string;
  growth_percent: number;
  growing_degree_days: string;
}

export interface IrrigationAdvisoryResult {
  alert: string;
  alert_sub: string;
  best_time: string;
  advisory_image: string;
  weather: {
    icon: string;
    weather_sub: string;
    weather_line: string;
  };
  icon?: string; // If appears independently
}

export interface SpraySlot {
  start: string;
  end: string;
  is_good: boolean;
}

export interface SprayingAdvisoryResult {
  today: {
    slots: SpraySlot[];
    weather_icon?: string;
  };
  tomorrow: {
    slots: SpraySlot[];
    weather_icon?: string | null;
  };
  advisory_image: string;
}

export interface SoilNutrient {
  name: string;
  amount: string;
  color: string;
}

export interface SoilNutrientAdvisoryResult {
  nutrients: SoilNutrient[];
  micronutrients: SoilNutrient[];
  advisory_image: string;
  micronutrients_image: string;
  error: string | null;
}

export interface RiskData {
  risk: "LOW" | "MEDIUM" | "HIGH";
  percent: number;
  sporecount: number;
}

export interface TimeSeriesPoint {
  time: string;
  pm: number;
  dm: number;
}

export interface TimeSeries {
  "24h": TimeSeriesPoint[];
  "48h": TimeSeriesPoint[];
  "7d": TimeSeriesPoint[];
  "30d": TimeSeriesPoint[];
}

export interface PathogenDetectionAdvisoryResult {
  last_updated: string;
  hourly_temperature: number;
  hourly_rh: number;
  hourly_dewpoint: number;
  hourly_pressure: number;
  hourly_vpd: number;

  pm: RiskData;
  dm: RiskData;

  timeseries: TimeSeries;
}
